import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Button, Space, Spin, message, Badge, Empty } from 'antd';
import { BulbOutlined, ArrowLeftOutlined, ClockCircleOutlined, CheckCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, Advice } from '../utils/api';

const { Title, Text } = Typography;

const AdviceManagePage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [advices, setAdvices] = useState<Advice[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState<number | null>(null);

  // 检查用户是否已登录且为医生
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    } else if (!authState.isLoading && authState.isAuthenticated && !authState.user?.is_doctor) {
      message.error('此页面仅供医生访问');
      navigate('/');
    }
  }, [authState.isLoading, authState.isAuthenticated, authState.user, navigate]);

  // 加载建议列表
  useEffect(() => {
    const loadAdvices = async () => {
      if (!authState.token) {
        return;
      }

      try {
        setLoading(true);
        const response = await api.getAdviceList(authState.token);
        
        if (response.status === 200 && response.data) {
          setAdvices(response.data.advices || []);
        } else {
          message.error(response.msg || '获取建议列表失败');
        }
      } catch (error) {
        console.error('获取建议列表失败:', error);
        message.error('获取建议列表失败');
      } finally {
        setLoading(false);
      }
    };

    if (authState.isAuthenticated && authState.token && authState.user?.is_doctor) {
      loadAdvices();
    }
  }, [authState.isAuthenticated, authState.token, authState.user]);

  // 返回主页
  const handleBack = () => {
    navigate('/');
  };

  // 接受建议
  const handleAcceptAdvice = async (adviceId: number) => {
    // 使用浏览器原生确认框
    const confirmed = window.confirm('确定要接受这条建议吗？接受后该用户将获得一张优惠券。');
    
    if (!confirmed) {
      return;
    }

    if (!authState.token) {
      message.error('请先登录');
      return;
    }

    try {
      setAcceptingId(adviceId);
      const response = await api.acceptAdvice(
        { advice_id: adviceId },
        authState.token
      );

      if (response.status === 200) {
        message.success('建议已接受，用户将获得优惠券奖励');
        // 重新加载建议列表
        const reloadResponse = await api.getAdviceList(authState.token);
        if (reloadResponse.status === 200 && reloadResponse.data) {
          setAdvices(reloadResponse.data.advices || []);
        }
      } else {
        message.error(response.msg || '接受建议失败');
      }
    } catch (error) {
      console.error('接受建议失败:', error);
      message.error('接受建议失败');
    } finally {
      setAcceptingId(null);
    }
  };

  // 格式化时间
  const formatTime = (timeString: string) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 计算待处理的建议数量
  const pendingCount = advices.filter(a => !a.Accepted).length;

  // 如果正在加载
  if (authState.isLoading || loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录或不是医生
  if (!authState.isAuthenticated || !authState.user?.is_doctor) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '40px'
      }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          size="large"
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          返回主页
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BulbOutlined style={{ fontSize: '32px', color: 'white' }} />
          <Title level={2} style={{ color: 'white', margin: 0 }}>
            建议管理
          </Title>
        </div>
        <div style={{ width: '100px' }} /> {/* 占位符保持居中 */}
      </div>

      {/* 建议统计 */}
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '40px',
        textAlign: 'center'
      }}>
        <Text style={{ color: 'white', fontSize: '18px' }}>
          共有 {advices.length} 条建议，其中 {pendingCount} 条待处理
        </Text>
      </div>

      {/* 建议列表 */}
      <Row gutter={[24, 24]} style={{ width: '100%', maxWidth: '1200px' }}>
        {advices.length === 0 ? (
          <Col span={24}>
            <Card style={{
              borderRadius: '16px',
              textAlign: 'center',
              padding: '40px'
            }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text style={{ color: '#999', fontSize: '16px' }}>
                    暂无建议
                  </Text>
                }
              />
            </Card>
          </Col>
        ) : (
          advices.map((advice) => (
            <Col xs={24} sm={24} md={24} lg={24} key={advice.ID}>
              <Card
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  border: advice.Accepted ? '2px solid #52c41a' : '2px solid #1890ff',
                  background: advice.Accepted ? '#f6ffed' : '#ffffff',
                }}
                bodyStyle={{
                  padding: '24px',
                }}
              >
                {/* 建议头部 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <Space>
                    <UserOutlined style={{
                      fontSize: '20px',
                      color: advice.Accepted ? '#52c41a' : '#1890ff'
                    }} />
                    <Text strong style={{ fontSize: '16px' }}>
                      用户ID: {advice.UserId}
                    </Text>
                  </Space>
                  {advice.Accepted ? (
                    <Badge 
                      status="success" 
                      text={
                        <Text strong style={{ color: '#52c41a' }}>
                          已接受
                        </Text>
                      } 
                    />
                  ) : (
                    <Badge 
                      status="processing" 
                      text={
                        <Text strong style={{ color: '#1890ff' }}>
                          待处理
                        </Text>
                      } 
                    />
                  )}
                </div>

                {/* 建议内容 */}
                <div style={{
                  background: '#fafafa',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <Text style={{
                    fontSize: '15px',
                    lineHeight: '1.8',
                    display: 'block',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {advice.Advice}
                  </Text>
                </div>

                {/* 建议信息 */}
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#666'
                  }}>
                    <ClockCircleOutlined style={{ marginRight: '8px' }} />
                    <Text style={{ color: '#666' }}>
                      提交时间：{formatTime(advice.CreatedAt)}
                    </Text>
                  </div>
                  
                  {advice.Accepted && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#52c41a'
                    }}>
                      <CheckCircleOutlined style={{ marginRight: '8px' }} />
                      <Text style={{ color: '#52c41a' }}>
                        已于 {formatTime(advice.UpdatedAt)} 接受
                      </Text>
                    </div>
                  )}
                </Space>

                {/* 操作按钮 */}
                {!advice.Accepted && (
                  <div style={{ marginTop: '16px' }}>
                    <Button
                      type="primary"
                      size="large"
                      loading={acceptingId === advice.ID}
                      onClick={() => handleAcceptAdvice(advice.ID)}
                      style={{
                        height: '40px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        background: '#52c41a',
                        borderColor: '#52c41a'
                      }}
                    >
                      接受建议
                    </Button>
                  </div>
                )}
              </Card>
            </Col>
          ))
        )}
      </Row>

      {/* 底部说明 */}
      <div style={{ 
        marginTop: '60px', 
        maxWidth: '800px',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Title level={4} style={{ color: '#1890ff', marginBottom: '20px' }}>
          说明
        </Title>
        <div style={{ textAlign: 'left', lineHeight: '1.8' }}>
          <Text style={{ color: '#595959', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
            • 这里显示所有用户提交的建议，您可以审核并接受有价值的建议
          </Text>
          <Text style={{ color: '#595959', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
            • 接受建议后，该用户将自动获得一张咖啡优惠券作为奖励
          </Text>
          <Text style={{ color: '#595959', fontSize: '14px', display: 'block' }}>
            • 已接受的建议不能重复接受，请谨慎操作
          </Text>
        </div>
      </div>
    </div>
  );
};

export default AdviceManagePage;


